import { action, computed, observable } from "mobx";
import { searchRequest } from "../utils/RestMiddlewareUtil";
import customerStore from "./CustomerStore";
import gtmService from "../services/GTMService";
import { getListingPageName } from "../utils/SearchUtils";
import { mapToStandardAnalyticsProducts } from "../utils/ProductUtils";

class SearchProductsStore {
  @observable all = [];
  @observable facets = [];
  @observable lastPage;
  @observable per = 30;
  @observable page = 0;
  @observable searchQuery = "";
  @observable totalRecords = 0;
  @observable state = "pending";
  @observable selectedFacet = "";

  query = "";
  promise;

  @computed get vendorId() {
    return customerStore.vendorId;
  }

  @computed get customer() {
    return customerStore.customer;
  }

  fetch(query, selectedFacet = "") {
    this.cancel();
    this.query = query;
    if (selectedFacet) {
      this.selectedFacet = selectedFacet;
    }
    this.fetchInternal();
  }

  @action
  cancelPromise() {
    if (this.promise) {
      this.promise.cancel();
    }
    this.state = "pending";
  }

  @action
  fetchInternal() {
    this.cancelPromise();

    if (!this.query) {
      this.cancelAll();
      return;
    }

    // Check if the query is a numeric product ID
    const queryTrimmed = this.query.trim();
    if (!isNaN(queryTrimmed) && queryTrimmed.length > 0) {
      // This looks like a product ID, try to fetch the product directly
      const productId = parseInt(queryTrimmed, 10);
      if (productId > 0) {
        // Redirect to product detail page with the ID
        window.location.href = `/products/product-${productId}/`;
        return;
      }
    }

    searchRequest(
      this,
      {
        query: this.query,
        page: this.page,
        per: this.per,
        category: this.selectedFacet || undefined
      },
      (data, pagination = {}) => {
        // The API returns data as an array directly. Search documents use the
        // vendor product id as `id` and carry the real id in `product_id`;
        // every other listing (and the cart, availability and order APIs)
        // expects `id` to be the product id, so normalise them here.
        const products = (Array.isArray(data) ? data : []).map(product =>
          product && product.product_id != null
            ? {
                ...product,
                id: product.product_id,
                vendor_product_id: product.vendor_product_id || product.id
              }
            : product
        );

        // Check if this might be a product identifier search
        if (this.page === 0 && products.length === 1) {
          const product = products[0];
          console.log("Search result product:", product);
          console.log("Query:", this.query);

          // Check if the product name or seo_url exactly matches the query
          const queryLower = this.query.toLowerCase().trim();
          const productNameLower = (product.name || "").toLowerCase().trim();
          const productSeoUrlLower = (product.seo_url || "")
            .toLowerCase()
            .trim();

          console.log("Query lower:", queryLower);
          console.log("Product name lower:", productNameLower);
          console.log("Product seo_url lower:", productSeoUrlLower);

          // Check for exact matches or if the query is a product identifier
          if (
            productNameLower === queryLower ||
            productSeoUrlLower === queryLower ||
            productSeoUrlLower.includes(queryLower) ||
            queryLower.includes(productSeoUrlLower)
          ) {
            // This appears to be a product identifier, redirect to product detail
            const productId = product.product_id || product.id;
            console.log("Product ID for redirect:", productId);
            if (productId) {
              // Use seo_url if available, otherwise use name, and append product ID
              const productSlug = product.seo_url || product.name;
              const cleanSlug = productSlug
                .replace(/[^a-zA-Z0-9-]/g, "-")
                .toLowerCase();
              const redirectUrl = `/products/${cleanSlug}-${productId}/`;
              console.log("Redirecting to:", redirectUrl);
              window.location.href = redirectUrl;
              return;
            }
          }
        }

        // Also check if any product in the results has a seo_url that exactly matches the query
        if (this.page === 0 && products.length > 0) {
          const queryLower = this.query.toLowerCase().trim();
          console.log(
            "Checking for exact matches in search results. Query:",
            queryLower
          );
          console.log(
            "All products:",
            products.map(p => ({
              name: p.name,
              seo_url: p.seo_url,
              product_id: p.product_id,
              id: p.id
            }))
          );

          const exactMatch = products.find(product => {
            const productSeoUrlLower = (product.seo_url || "")
              .toLowerCase()
              .trim();
            return productSeoUrlLower === queryLower;
          });

          if (exactMatch) {
            console.log("Found exact match:", exactMatch);
            const productId = exactMatch.product_id || exactMatch.id;
            console.log("Product ID for exact match redirect:", productId);
            if (productId) {
              const productSlug = exactMatch.seo_url || exactMatch.name;
              const cleanSlug = productSlug
                .replace(/[^a-zA-Z0-9-]/g, "-")
                .toLowerCase();
              const redirectUrl = `/products/${cleanSlug}-${productId}/`;
              console.log("Redirecting to (exact match):", redirectUrl);
              window.location.href = redirectUrl;
              return;
            }
          }
        }

        this.all = this.all.slice().concat(products);
        // Pages are 0-based, so the last page index is total_pages - 1; handleScroll
        // stops loading more once page reaches it.
        this.lastPage = Math.max((pagination.total_pages || 0) - 1, 0);
        this.totalRecords =
          pagination.total != null ? pagination.total : products.length;
        this.searchQuery = this.query;

        // Handle facets/categories if provided by the new API
        if (data.facets && data.facets.category_name && !this.selectedFacet) {
          this.facets = Object.entries(data.facets.category_name);
        } else if (data.categories && !this.selectedFacet) {
          // If the new API provides categories in a different format
          this.facets = data.categories.map(cat => [cat.name, cat.count]);
        }

        if (this.totalRecords === 0) {
          gtmService.event(
            "Search",
            "Not found",
            this.query,
            this.totalRecords
          );
        } else {
          gtmService.event(
            "Search",
            "Found",
            this.query,
            this.totalRecords,
            mapToStandardAnalyticsProducts(products)
          );
          gtmService.productImpressions(
            products,
            getListingPageName(),
            this.customer,
            "Search"
          );
        }
      }
    );
  }

  @action
  loadMore() {
    this.page++;
    this.fetchInternal();
  }

  @action
  cancel() {
    this.all = [];
    this.selectedFacet = "";
    this.per = 30;
    this.page = 0;
    this.state = "pending";
    if (this.promise) {
      this.promise.cancel();
    }
  }

  @action
  cancelAll() {
    this.cancel();
    this.totalRecords = 0;
    this.searchQuery = "";
  }
}

const searchProductsStore = new SearchProductsStore();
export default searchProductsStore;
